import { useQuery } from '@tanstack/vue-query';
import { api } from '@/hooks/axios-api';
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { type RolloverData, type TreatmentPlanRequest } from '@/types/treatment-plan';
import { ensureArray }  from '@/utils/utils';

export const fetchTreatmentPlan = async (jsonBody: any) => {

  const response = await api.post('/tools/treatment-plan/', jsonBody);
  return response.data;
};


const stageTariffCodeRollover = async (
  status: string,
  programmePhaseId: any,
  toYear: string,
  sunset: string
) => {
 const params = new URLSearchParams({
    status: status,
    programme_phase_id: programmePhaseId,
    to_year: toYear,
    sunset: sunset
  });
  const response = await api.get('/tools/stage-rollover', {params});
  return response.data;
}

// Treatment Plan Rollover
const tariffCodesRollover = async (
  sunset: string, 
  toYear: string,
  payload: RolloverData) => {

    const params = new URLSearchParams({sunset: sunset, to_year: toYear});
    const response = await api.put(`/tools/rollover/?${params}`, ensureArray(payload));
    return response.data
}

export const syncPracticeTypes = async (jsonBody: any) => {
  const response = await api.put('/tools/sync-practice-types/', jsonBody);
  return response.data;
};

export const bulkProcessUploadTcToTp = async (jsonBody: any) => {
  const response = await api.post('/tools/ihub-bulk-upload-tctotp/', jsonBody);
  return response.data;
};

export const bulkProcessMergeTc = async (jsonBody: any) => {
  const response = await api.post('/tools/ihub-bulk-merge-tc/', jsonBody);
  return response.data;
};

export const bulkProcessTcAllowedQty = async (jsonBody: any) => {
  const response = await api.post('/tools/ihub-bulk-tc-allowed-qty/', jsonBody);
  return response.data;
};

export const bulkProcessSunsetTc = async (jsonBody: any) => {
  const response = await api.post('/tools/ihub-bulk-update-tc-end-date/', jsonBody);
  return response.data;
};

////////////////////////////////// HOOKS //////////////////////////// 

export const useBulkProcessMergeTc = () => useMutation({
  mutationFn: (jsonBody: any) => bulkProcessMergeTc(jsonBody),
})

export const useBulkProcessUploadTcToTp = () => useMutation({
  mutationFn: (jsonBody: any) => bulkProcessUploadTcToTp(jsonBody),
})

export const useBulkProcessTcAllowedQty = () => useMutation({
  mutationFn: (jsonBody: any) => bulkProcessTcAllowedQty(jsonBody),
})

export const useBulkProcessSunsetTc = () => useMutation({
  mutationFn: (jsonBody: any) => bulkProcessSunsetTc(jsonBody),
})


export const useStageTariffCodeRollover = (
  status:string,
  programmePhaseId: string,
  toYear: string, 
  sunset: string) => useQuery({
    queryKey: ["eligibleHealthSeekers"],
    queryFn: () => stageTariffCodeRollover(
      status, 
      programmePhaseId,
      toYear, 
      sunset
    ),
});

export const useTariffCodesRollover = () => {
  
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { sunset: string; toYear: string; payload: RolloverData }) =>
      tariffCodesRollover(params.sunset, params.toYear, params.payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tariffCodesRollover'] })
    },
    onError: (error: any) => {
      console.error('Rollover failed:', error)
    },
  })
}

export const useSyncPracticeTypes = () => {
  
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { payload: any }) =>
      syncPracticeTypes(params.payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['syncPracticeTypes'] })
    },
    onError: (error: any) => {
      console.error('Sync Practice Types failed:', error)
    },
  })
}

export const useFetchTreatmentPlan  = (jsonBody: any) => useQuery({
  queryKey: ["treatmentPlan"],
  queryFn: () => fetchTreatmentPlan(jsonBody),
});

export const useTreatmentPlan = () => useMutation({
  mutationFn: (payload: TreatmentPlanRequest) => fetchTreatmentPlan(payload),
});

